import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AttachFilesSection } from '@/components/shared'
import type { AttachmentRef } from '@/components/shared'
import { quotesApi } from '@/services/quotes'
import { clientsApi } from '@/services/clients'
import { settingsApi } from '@/services/settings'
import { getApiErrorMessage } from '@/services/api'
import { toast } from '@/components/ui/toast'
import { DOCUMENT_FILE_ACCEPT } from '@/lib/constants'

interface Props {
  quoteId: string
  clientId?: string
  hideHeading?: boolean
  brandTabs?: boolean
  /** Read-only file cards with view/download (quote detail). */
  readOnly?: boolean
}

function attachmentSource(
  docType: string | null | undefined
): AttachmentRef['source'] {
  if (docType === 'client' || docType === 'library') return docType
  return 'upload'
}

// Quote attachments (feedback #31/#32). Wraps the shared AttachFilesSection,
// wiring it to the quote document endpoints (upload new / pick from the
// client's documents / pick from the Settings document library). Persists each
// change immediately and keeps the attached list in sync. Used on both the
// quote edit form and the quote detail screen.
export function QuoteAttachments({
  quoteId,
  clientId,
  hideHeading,
  brandTabs,
  readOnly,
}: Props) {
  const { t } = useTranslation('quotes')
  const [value, setValue] = useState<AttachmentRef[]>([])

  // Load the currently-attached documents.
  useEffect(() => {
    let active = true
    quotesApi
      .listDocuments(quoteId, { limit: 100 })
      .then(res => {
        if (!active) return
        setValue(
          (res.data ?? []).map(d => ({
            id: d.id,
            file_id: d.file_id,
            name: d.file_name ?? t('attachments.untitled'),
            url: d.url,
            mime_type: d.file_type,
            size: d.file_size,
            created_at: d.created_at,
            source: attachmentSource(d.doc_type),
          }))
        )
      })
      .catch(() => {
        /* leave empty on failure */
      })
    return () => {
      active = false
    }
  }, [quoteId, t])

  const uploadFn = useCallback(
    async (file: File): Promise<AttachmentRef> => {
      const res = await quotesApi.uploadDocument(quoteId, file)
      const d = res.data!
      return {
        id: d.id,
        file_id: d.file_id,
        name: d.file_name ?? file.name,
        url: d.url,
        mime_type: d.file_type,
        size: d.file_size,
        created_at: d.created_at,
        source: 'upload',
      }
    },
    [quoteId]
  )

  const listClientDocsFn = useCallback(
    async (cId: string): Promise<AttachmentRef[]> => {
      const res = await clientsApi.documents(cId, { limit: 100 })
      return (res.data ?? []).map(d => ({
        // Use the underlying file id so the quote links the same file.
        id: d.file_id,
        file_id: d.file_id,
        name: d.file_name,
        url: d.url,
        mime_type: d.file_type,
        size: d.file_size,
        created_at: d.created_at,
        source: 'client' as const,
      }))
    },
    []
  )

  const listLibraryFn = useCallback(async (): Promise<AttachmentRef[]> => {
    const res = await settingsApi.reusableDocumentOptions('quote')
    return (res.data ?? []).map(d => ({
      id: d.file_id,
      file_id: d.file_id,
      name: d.name,
      created_at: d.created_at,
      source: 'library' as const,
    }))
  }, [])

  // Persist add (link existing) / remove against the quote.
  const onChange = useCallback(
    async (next: AttachmentRef[]) => {
      const prev = value
      // Optimistic update.
      setValue(next)

      const added = next.filter(n => !prev.some(p => sameRef(p, n)))
      const removed = prev.filter(p => !next.some(n => sameRef(p, n)))

      try {
        for (const ref of added) {
          // Freshly-uploaded refs are already persisted by uploadFn (they carry
          // a quote-document id and 'upload' source); only client/library picks
          // need a link call.
          if (ref.source === 'upload') continue
          if (!ref.id) continue
          const res = await quotesApi.attachExistingDocument(
            quoteId,
            ref.id,
            ref.source
          )
          // Swap the local ref id to the new quote-document id so removal works.
          setValue(curr =>
            curr.map(c => (sameRef(c, ref) ? { ...c, id: res.data?.id } : c))
          )
        }
        for (const ref of removed) {
          if (ref.id) await quotesApi.removeDocument(quoteId, ref.id)
        }
      } catch (e) {
        // Roll back on failure.
        setValue(prev)
        toast({
          variant: 'destructive',
          title: getApiErrorMessage(e, t('attachments.error')),
        })
      }
    },
    [quoteId, value, t]
  )

  const onRename = useCallback(
    async (item: AttachmentRef, name: string) => {
      if (item.source !== 'upload' || !item.id) return
      const response = await quotesApi.renameDocument(quoteId, item.id, name)
      toast({ variant: 'success', title: t('attachments.renameSuccess') })
      return {
        name: response.data?.file_name ?? item.name,
        url: response.data?.url ?? item.url,
      }
    },
    [quoteId, t]
  )

  if (readOnly) {
    return (
      <AttachFilesSection
        value={value}
        onChange={() => undefined}
        hideHeading={hideHeading}
        readOnly
        emptyMessage={t('attachments.none')}
      />
    )
  }

  return (
    <AttachFilesSection
      value={value}
      onChange={onChange}
      clientId={clientId}
      accept={DOCUMENT_FILE_ACCEPT}
      uploadFn={uploadFn}
      listClientDocsFn={clientId ? listClientDocsFn : undefined}
      listLibraryFn={listLibraryFn}
      libraryTarget='quote'
      preloadLibrary
      onRename={onRename}
      hideHeading={hideHeading}
      brandTabs={brandTabs}
    />
  )
}

// Identity across optimistic updates: match on id when present, else name+url.
function sameRef(a: AttachmentRef, b: AttachmentRef): boolean {
  if (a.file_id && b.file_id) return a.file_id === b.file_id
  if (a.id && b.id) return a.id === b.id
  return a.name === b.name && (a.url ?? '') === (b.url ?? '')
}

export default QuoteAttachments
